import { useTheme } from '@repo/ui/components/theme-provider'
import type { Feature, FeatureCollection, Geometry, Point } from 'geojson'
import { MapPinned } from 'lucide-react'
import maplibregl, { type GeoJSONSource, type MapLayerMouseEvent } from 'maplibre-gl'
import { useEffect, useMemo, useRef, useState } from 'react'
import {
  createMap,
  createMapStyle,
  escapeMapHtml,
  fitMapToPoints,
  invalidateMapSize,
  type LngLat,
  latLngToLngLat,
  syncMapWhenStyleIsReady,
} from '@/lib/maplibre'

interface Appointment {
  id: string
  date: string
  startTime: string
  endTime: string
  addressText: string | null
  lat: string | null
  lng: string | null
  service?: { name: string } | null
  customer?: { name: string } | null
}

interface DayRouteMapProps {
  appointments: Appointment[]
  coverageData?: FeatureCollection<Geometry, object> | null
  selectedId?: string | null
  onSelectAppointment?: (id: string) => void
}

type AppointmentProperties = {
  id: string
  title: string
  time: string
  customer: string
  addressText: string
  selected: boolean
}

const emptyAppointments: FeatureCollection<Point, AppointmentProperties> = {
  type: 'FeatureCollection',
  features: [],
}
const emptyCoverage: FeatureCollection<Geometry, Record<string, never>> = {
  type: 'FeatureCollection',
  features: [],
}

export function DayRouteMap({
  appointments,
  coverageData: providedCoverageData,
  selectedId,
  onSelectAppointment,
}: DayRouteMapProps) {
  const containerRef = useRef<HTMLDivElement | null>(null)
  const mapRef = useRef<maplibregl.Map | null>(null)
  const popupRef = useRef<maplibregl.Popup | null>(null)
  const [mapError, setMapError] = useState(false)
  const { resolvedTheme } = useTheme()
  const isDark = resolvedTheme === 'dark'

  const geoAppointments = useMemo(
    () =>
      appointments
        .filter((appointment) => appointment.lat && appointment.lng)
        .map((appointment) => ({
          ...appointment,
          position: [Number(appointment.lat), Number(appointment.lng)] as [number, number],
        }))
        .filter(
          (appointment) =>
            Number.isFinite(appointment.position[0]) && Number.isFinite(appointment.position[1])
        )
        .sort((left, right) => left.startTime.localeCompare(right.startTime)),
    [appointments]
  )
  const appointmentData = useMemo<FeatureCollection<Point, AppointmentProperties>>(
    () => ({
      type: 'FeatureCollection',
      features: geoAppointments.map((appointment, index) => ({
        type: 'Feature',
        geometry: {
          type: 'Point',
          coordinates: latLngToLngLat(appointment.position),
        },
        properties: {
          id: appointment.id,
          title: `${index + 1}. ${appointment.service?.name ?? 'Appointment'}`,
          time: `${appointment.startTime} - ${appointment.endTime}`,
          customer: appointment.customer?.name ?? '',
          addressText: appointment.addressText ?? '',
          selected: appointment.id === selectedId,
        },
      })),
    }),
    [geoAppointments, selectedId]
  )
  const fitPoints = useMemo<LngLat[]>(
    () => geoAppointments.map((appointment) => latLngToLngLat(appointment.position)),
    [geoAppointments]
  )
  const coverageData = providedCoverageData ?? emptyCoverage
  const coverageFitPoints = useMemo<LngLat[]>(
    () => collectCoveragePoints(coverageData),
    [coverageData]
  )
  const firstPosition = geoAppointments[0]?.position
  const fallbackCenter = useMemo<[number, number]>(
    () => firstPosition ?? lngLatToLatLng(coverageFitPoints[0]) ?? [39.8283, -98.5795],
    [coverageFitPoints, firstPosition]
  )
  const fallbackZoom = firstPosition ? 13 : coverageFitPoints.length > 0 ? 10 : 4
  const viewportPoints = fitPoints.length > 0 ? fitPoints : coverageFitPoints

  useEffect(() => {
    const node = containerRef.current
    if (!node || mapRef.current) return

    let map: maplibregl.Map
    try {
      map = createMap({
        container: node,
        center: fallbackCenter,
        zoom: fallbackZoom,
        isDark,
      })
    } catch {
      setMapError(true)
      return
    }

    setMapError(false)
    mapRef.current = map

    map.on('load', () => {
      addAppointmentSourcesAndLayers(map, isDark)
      setMapSourceData(map, 'route-coverage', coverageData)
      setAppointmentSourceData(map, appointmentData)
      fitMapToPoints({
        map,
        points: viewportPoints,
        fallbackCenter,
        fallbackZoom,
        maxZoom: 14,
        padding: 40,
      })
    })

    return () => {
      popupRef.current?.remove()
      popupRef.current = null
      map.remove()
      mapRef.current = null
    }
  }, [appointmentData, coverageData, fallbackCenter, fallbackZoom, isDark, viewportPoints])

  useEffect(() => {
    const map = mapRef.current
    if (!map) return
    map.setStyle(createMapStyle(isDark))
  }, [isDark])

  useEffect(() => {
    const map = mapRef.current
    if (!map) return

    return syncMapWhenStyleIsReady(map, () => {
      addAppointmentSourcesAndLayers(map, isDark)
      setMapSourceData(map, 'route-coverage', coverageData)
      setAppointmentSourceData(map, appointmentData)
    })
  }, [appointmentData, coverageData, isDark])

  useEffect(() => {
    const map = mapRef.current
    if (!map) return

    const handleClick = (event: MapLayerMouseEvent) => {
      const feature = event.features?.[0] as Feature<Point, AppointmentProperties> | undefined
      if (!feature) return
      onSelectAppointment?.(feature.properties.id)
      popupRef.current?.remove()
      popupRef.current = new maplibregl.Popup({ closeButton: false, maxWidth: '240px' })
        .setLngLat(event.lngLat)
        .setHTML(renderAppointmentPopup(feature.properties))
        .addTo(map)
    }
    const handleEnter = () => {
      map.getCanvas().style.cursor = 'pointer'
    }
    const handleLeave = () => {
      map.getCanvas().style.cursor = ''
    }

    map.on('click', 'appointment-point', handleClick)
    map.on('mouseenter', 'appointment-point', handleEnter)
    map.on('mouseleave', 'appointment-point', handleLeave)
    return () => {
      map.off('click', 'appointment-point', handleClick)
      map.off('mouseenter', 'appointment-point', handleEnter)
      map.off('mouseleave', 'appointment-point', handleLeave)
    }
  }, [onSelectAppointment])

  useEffect(() => {
    const map = mapRef.current
    const node = containerRef.current
    if (!map || !node) return

    setAppointmentSourceData(map, appointmentData)
    setMapSourceData(map, 'route-coverage', coverageData)
    fitMapToPoints({
      map,
      points: viewportPoints,
      fallbackCenter,
      fallbackZoom,
      maxZoom: 14,
      padding: 40,
    })
    const selectedFeature = appointmentData.features.find(
      (feature: Feature<Point, AppointmentProperties>) => feature.properties.selected
    )
    if (selectedFeature) {
      popupRef.current?.remove()
      popupRef.current = new maplibregl.Popup({ closeButton: false, maxWidth: '240px' })
        .setLngLat(selectedFeature.geometry.coordinates as LngLat)
        .setHTML(renderAppointmentPopup(selectedFeature.properties))
        .addTo(map)
    }

    const resize = () => invalidateMapSize(map)
    const resizeObserver = new ResizeObserver(resize)
    resizeObserver.observe(node)
    window.setTimeout(resize, 80)

    return () => resizeObserver.disconnect()
  }, [appointmentData, coverageData, fallbackCenter, fallbackZoom, viewportPoints])

  if (mapError) {
    return (
      <output className="flex h-full min-h-[18rem] items-center justify-center overflow-hidden rounded-[6px] border border-border/70 bg-muted/20 px-6 text-center">
        <div className="max-w-xs">
          <div className="mx-auto flex h-10 w-10 items-center justify-center rounded-[6px] border border-border/70 bg-background text-muted-foreground">
            <MapPinned className="h-5 w-5" />
          </div>
          <div className="mt-4 text-sm font-semibold text-foreground">Map unavailable</div>
          <p className="mt-2 text-xs leading-5 text-muted-foreground">
            Appointment details still work. Reopen this route map from a browser with WebGL enabled.
          </p>
        </div>
      </output>
    )
  }

  return (
    <div
      ref={containerRef}
      className="h-full min-h-[18rem] overflow-hidden rounded-[6px] border border-border/70 bg-muted/20"
      role="img"
      aria-label="Appointment map"
    />
  )
}

function addAppointmentSourcesAndLayers(map: maplibregl.Map, isDark: boolean) {
  if (!map.isStyleLoaded()) return

  const pointColor = isDark ? '#b87b63' : '#9d654f'
  const strokeColor = isDark ? '#17130f' : '#f4f1eb'
  const coverageFillColor = isDark ? '#4f83b8' : '#4f83b8'
  const coverageLineColor = isDark ? '#8fb5df' : '#366b9f'

  if (!map.getSource('route-coverage')) {
    map.addSource('route-coverage', { type: 'geojson', data: emptyCoverage })
  }
  if (!map.getLayer('route-coverage-fill')) {
    map.addLayer({
      id: 'route-coverage-fill',
      type: 'fill',
      source: 'route-coverage',
      paint: {
        'fill-color': coverageFillColor,
        'fill-opacity': isDark ? 0.14 : 0.1,
      },
    })
  }
  map.setPaintProperty('route-coverage-fill', 'fill-color', coverageFillColor)
  map.setPaintProperty('route-coverage-fill', 'fill-opacity', isDark ? 0.14 : 0.1)
  if (!map.getLayer('route-coverage-line')) {
    map.addLayer({
      id: 'route-coverage-line',
      type: 'line',
      source: 'route-coverage',
      layout: {
        'line-cap': 'round',
        'line-join': 'round',
      },
      paint: {
        'line-color': coverageLineColor,
        'line-opacity': isDark ? 0.7 : 0.58,
        'line-width': 1,
      },
    })
  }
  map.setPaintProperty('route-coverage-line', 'line-color', coverageLineColor)
  map.setPaintProperty('route-coverage-line', 'line-opacity', isDark ? 0.18 : 0.14)
  map.setPaintProperty('route-coverage-line', 'line-width', 0.5)

  if (!map.getSource('appointments')) {
    map.addSource('appointments', { type: 'geojson', data: emptyAppointments })
  }
  if (!map.getLayer('appointment-point')) {
    map.addLayer({
      id: 'appointment-point',
      type: 'circle',
      source: 'appointments',
      paint: {
        'circle-radius': ['case', ['get', 'selected'], 8, 6],
        'circle-color': '#9d654f',
        'circle-stroke-color': '#f4f1eb',
        'circle-stroke-width': ['case', ['get', 'selected'], 3, 2],
      },
    })
  }
  map.setPaintProperty('appointment-point', 'circle-radius', ['case', ['get', 'selected'], 8, 6])
  map.setPaintProperty('appointment-point', 'circle-color', pointColor)
  map.setPaintProperty('appointment-point', 'circle-stroke-color', strokeColor)
  map.setPaintProperty('appointment-point', 'circle-stroke-width', [
    'case',
    ['get', 'selected'],
    3,
    2,
  ])
}

function setAppointmentSourceData(
  map: maplibregl.Map,
  data: FeatureCollection<Geometry, AppointmentProperties>
) {
  setMapSourceData(map, 'appointments', data)
}

function setMapSourceData(
  map: maplibregl.Map,
  sourceId: string,
  data: FeatureCollection<Geometry, object>
) {
  const source = map.getSource(sourceId) as GeoJSONSource | undefined
  source?.setData(data)
}

function collectCoveragePoints(data: FeatureCollection<Geometry, object>): LngLat[] {
  const points: LngLat[] = []

  for (const feature of data.features) {
    if (feature.geometry.type === 'Polygon') {
      for (const ring of feature.geometry.coordinates) {
        for (const coordinate of ring) points.push(coordinate as LngLat)
      }
    }
    if (feature.geometry.type === 'MultiPolygon') {
      for (const polygon of feature.geometry.coordinates) {
        for (const ring of polygon) {
          for (const coordinate of ring) points.push(coordinate as LngLat)
        }
      }
    }
  }

  return points
}

function lngLatToLatLng(point: LngLat | undefined): [number, number] | null {
  if (!point) return null
  return [point[1], point[0]]
}

function renderAppointmentPopup(properties: AppointmentProperties) {
  return `
    <div style="min-width: 180px; color: var(--card-foreground);">
      <div style="font-weight: 600">${escapeMapHtml(properties.title)}</div>
      <div>${escapeMapHtml(properties.time)}</div>
      ${properties.customer ? `<div>${escapeMapHtml(properties.customer)}</div>` : ''}
      ${properties.addressText ? `<div style="max-width: 220px; color: var(--muted-foreground); font-size: 12px">${escapeMapHtml(properties.addressText)}</div>` : ''}
    </div>
  `
}
